import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Truck, FileSignature, BarChart2, MessageSquare, Home, Lock } from 'lucide-react';
import { useUnreadMessages } from '@/contexts/UnreadMessagesContext';
import { useDemoAccess } from '@/contexts/DemoAccessContext';
import useOrg from '@/contexts/OrgContext';
import { useActiveModuleKeys } from '@/hooks/useModuleEnabled';
import { partAccess, CREW_PATH_PART } from '@/lib/partSurfaces';

const NavItem = ({ to, icon: Icon, label, hasBadge }) => {
  const location = useLocation();
  const isActive = location.pathname.includes(to);
  // Demo scene lock: a part the presentation hasn't opened yet renders
  // frosted with a lock — no navigation until the presenter opens it.
  // Inert for real crews (the hook says everything's open).
  const { active, isRouteOpen } = useDemoAccess();
  const demoLocked = active && !isRouteOpen(to);

  if (demoLocked) {
    return (
      <div
        aria-disabled="true"
        className="flex flex-col items-center justify-center w-full pt-1 pb-1 text-[10px] font-medium text-gray-400 dark:text-gray-600 opacity-50 select-none"
      >
        <div className="relative">
          <Icon className="w-5 h-5 mb-0.5" />
          <Lock className="absolute -top-1 -right-2 h-3 w-3" />
        </div>
        <span>{label}</span>
      </div>
    );
  }

  return (
    <NavLink
      to={to}
      className={({ isActive: isNavLinkActive }) =>
        `flex flex-col items-center justify-center w-full pt-1 pb-1 text-[10px] transition-colors duration-200 ease-in-out relative ${
          isActive || isNavLinkActive
            ? 'text-blue-500 dark:text-blue-400'
            : 'text-gray-500 dark:text-gray-400 font-medium'
        }`
      }
    >
      <div className="relative">
        <Icon className="w-5 h-5 mb-0.5" />
        {hasBadge && (
          <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-indigo-400 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-indigo-600 border-2 border-white dark:border-slate-800" />
          </span>
        )}
      </div>
      <span>{label}</span>
    </NavLink>
  );
};

// The four around Home, in order. Each is a part's surface (Logs is
// Routes → Pay, Invoice is the Invoicing Engine, Perf is Performance
// Intelligence; Messages is the core); a crew whose operator has not bought
// a part does not get its tab, and Home stays in the middle of whatever is left.
export const BOTTOM_ITEMS = [
  { to: '/subcontractor/dashboard/logs', icon: Truck, label: 'Logs' },
  { to: '/subcontractor/dashboard/live-invoice', icon: FileSignature, label: 'Invoice' },
  { to: '/subcontractor/dashboard/messages', icon: MessageSquare, label: 'Msgs', badge: true },
  { to: '/subcontractor/dashboard/performance', icon: BarChart2, label: 'Perf' },
];

export function visibleBottomItems(items, access) {
  return items.filter((it) => partAccess(it.to, access, CREW_PATH_PART) === 'on');
}

const BottomNavBar = () => {
  const { unreadCount } = useUnreadMessages();
  const location = useLocation();
  const isHomeActive = location.pathname === '/subcontractor/dashboard';
  const { isFlagship, loaded: orgLoaded } = useOrg();
  const parts = useActiveModuleKeys();
  const items = visibleBottomItems(BOTTOM_ITEMS, {
    keys: parts.keys, loaded: parts.loaded && orgLoaded, isFlagship: orgLoaded && isFlagship,
  });
  const left = items.slice(0, Math.ceil(items.length / 2));
  const right = items.slice(left.length);
  const render = (it) => <NavItem key={it.to} to={it.to} icon={it.icon} label={it.label} hasBadge={!!it.badge && unreadCount > 0} />;

  return (
    <footer
      className="fixed bottom-0 left-0 z-50 w-full h-16 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-t border-gray-100 dark:border-slate-800 md:hidden"
      style={{ height: 'calc(4rem + env(safe-area-inset-bottom))', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="grid h-full max-w-lg mx-auto px-2" style={{ gridTemplateColumns: `repeat(${items.length + 1}, minmax(0, 1fr))` }}>
        {left.map(render)}

        {/* Central Home Button */}
        <div className="flex items-center justify-center relative -mt-5">
           <NavLink
             to="/subcontractor/dashboard"
             className={({ isActive }) => `
               flex flex-col items-center justify-center
               h-14 w-14 rounded-full
               shadow-[0_8px_25px_rgba(37,99,235,0.4)]
               border-4 border-white dark:border-slate-900
               transition-all duration-300 transform active:scale-95
               ${(isActive || isHomeActive) ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white/90'}
             `}
           >
             <Home className="w-7 h-7" />
           </NavLink>
        </div>

        {right.map(render)}
      </div>
    </footer>
  );
};

export default BottomNavBar;
